import {
  formatDuration,
  parseDuration,
  SOCIAL_FORMAT_LABELS,
  SOCIAL_FORMATS,
  SOCIAL_IDEA_STATUS_LABELS,
  SOCIAL_NETWORK_LABELS,
  SOCIAL_NETWORKS,
  socialFormatSchema,
  socialIdeaSchema,
  type SocialFormat,
  type SocialIdea,
  type SocialIdeaStatus,
  type SocialNetwork,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  cx,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  Textarea,
} from '@growup/ui';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';

const BASE = '/api/site/social/ideas';

const STATUS_TONE: Record<SocialIdeaStatus, 'warning' | 'success' | 'neutral'> = {
  suggested: 'warning',
  accepted: 'success',
  rejected: 'neutral',
};

/** O texto, a descricao e as etiquetas, sem acentos nem maiusculas. */
const fold = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

export const matchesIdea = (idea: SocialIdea, query: string) => {
  const q = fold(query.trim());
  return q === '' || fold([idea.title, idea.description, ...idea.tags].join(' ')).includes(q);
};

/**
 * Redes sociais > Banco de ideias (docs/redes-sociais). O Gestor de Redes
 * Sociais cria ideias ja no banco e aceita ou recusa as sugestoes; um
 * influencer ve o banco e sugere ideias, que pode mudar enquanto estao por
 * aceitar.
 */
export function IdeasView({ canManage }: { canManage: boolean }) {
  const [list, setList] = useState<SocialIdea[] | null>(null);
  const [status, setStatus] = useState<SocialIdeaStatus>('accepted');
  const [query, setQuery] = useState('');
  const [format, setFormat] = useState<SocialFormat | ''>('');
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getJson(BASE, z.array(socialIdeaSchema))
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const replace = (idea: SocialIdea) =>
    setList((l) =>
      l?.some((i) => i.id === idea.id)
        ? l.map((i) => (i.id === idea.id ? idea : i))
        : [idea, ...(l ?? [])],
    );

  const review = async (idea: SocialIdea, accept: boolean) => {
    const note = accept ? '' : window.prompt(`Porque recusa «${idea.title}»? (opcional)`, '');
    if (note === null) return;
    setBusy(true);
    setError(null);
    try {
      replace(
        socialIdeaSchema.parse(await send('POST', `${BASE}/${idea.id}/review`, { accept, note })),
      );
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  if (editing !== null) {
    return (
      <IdeaEditor
        idea={list?.find((i) => i.id === editing) ?? null}
        canManage={canManage}
        onSaved={(idea) => {
          replace(idea);
          setEditing(null);
        }}
        onClose={() => {
          setEditing(null);
          load();
        }}
      />
    );
  }

  const count = (s: SocialIdeaStatus) => list?.filter((i) => i.status === s).length ?? 0;
  const shown =
    list?.filter(
      (i) => i.status === status && (format === '' || i.format === format) && matchesIdea(i, query),
    ) ?? [];

  return (
    <Card
      headingLevel={2}
      title="Banco de ideias"
      actions={
        <Button size="sm" onClick={() => setEditing('new')}>
          {canManage ? 'Nova ideia' : 'Sugerir ideia'}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          {canManage
            ? 'As ideias para as redes sociais, para reaproveitar nos planos. As sugestões dos influencers ficam por aceitar.'
            : 'As ideias para as redes sociais. As que sugerir ficam por aceitar pelo Gestor de Redes Sociais.'}
        </p>
        <nav className="flex flex-wrap gap-1" aria-label="Estado das ideias">
          {(['accepted', 'suggested', 'rejected'] as const).map((s) => (
            <button
              key={s}
              type="button"
              aria-current={status === s ? 'page' : undefined}
              onClick={() => setStatus(s)}
              className={cx(
                'border-b-2 px-3 py-2 font-sub text-label font-bold uppercase',
                status === s
                  ? 'border-gu-accent text-gu-ink'
                  : 'border-transparent text-gu-ink-muted hover:text-gu-ink',
              )}
            >
              {SOCIAL_IDEA_STATUS_LABELS[s]} ({String(count(s))})
            </button>
          ))}
        </nav>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Procurar" optional>
            <Input
              value={query}
              placeholder="Título, descrição ou etiqueta"
              onChange={(e) => setQuery(e.target.value)}
            />
          </Field>
          <Field label="Formato" optional>
            <Select
              value={format}
              onChange={(e) =>
                setFormat(e.target.value === '' ? '' : socialFormatSchema.parse(e.target.value))
              }
            >
              <option value="">Todos</option>
              {SOCIAL_FORMATS.map((f) => (
                <option key={f} value={f}>
                  {SOCIAL_FORMAT_LABELS[f]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {list === null && !error ? <p>A carregar…</p> : null}
        {list !== null && shown.length === 0 ? <p>Nenhuma ideia aqui.</p> : null}
        <ul className="flex flex-col gap-3">
          {shown.map((idea) => (
            <li key={idea.id} className="flex flex-col gap-2 border-2 border-gu-line p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="font-semibold">{idea.title}</span>
                  <span className="text-caption text-gu-ink-muted">
                    {SOCIAL_FORMAT_LABELS[idea.format]}
                    {idea.durationSeconds ? ` · ${formatDuration(idea.durationSeconds)}` : ''}
                    {idea.networks.length > 0
                      ? ` · ${idea.networks.map((n) => SOCIAL_NETWORK_LABELS[n]).join(', ')}`
                      : ''}
                  </span>
                </div>
                {idea.status !== 'accepted' ? (
                  <StatusBadge tone={STATUS_TONE[idea.status]}>
                    {SOCIAL_IDEA_STATUS_LABELS[idea.status]}
                  </StatusBadge>
                ) : null}
              </div>
              {idea.description ? (
                <p className="whitespace-pre-line text-gu-ink-muted">{idea.description}</p>
              ) : null}
              {idea.tags.length > 0 ? (
                <p className="flex flex-wrap gap-1">
                  {idea.tags.map((t) => (
                    <span key={t} className="bg-gu-sunken px-2 py-0.5 text-caption">
                      #{t}
                    </span>
                  ))}
                </p>
              ) : null}
              {idea.status === 'rejected' && idea.reviewNote ? (
                <p className="text-caption">
                  Recusada por {idea.reviewedBy}: {idea.reviewNote}
                </p>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-auto text-caption text-gu-ink-muted">
                  {idea.status === 'suggested' ? 'Sugerida' : 'Criada'} por {idea.createdBy} a{' '}
                  {formatDateTime(idea.createdAt)}
                </span>
                {canManage && idea.status === 'suggested' ? (
                  <>
                    <Button size="sm" disabled={busy} onClick={() => void review(idea, true)}>
                      Aceitar
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => void review(idea, false)}
                    >
                      Recusar
                    </Button>
                  </>
                ) : null}
                {idea.canEdit ? (
                  <Button size="sm" variant="ghost" onClick={() => setEditing(idea.id)}>
                    Editar
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

const lines = (text: string) =>
  text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '');

function IdeaEditor({
  idea,
  canManage,
  onSaved,
  onClose,
}: {
  idea: SocialIdea | null;
  canManage: boolean;
  onSaved: (idea: SocialIdea) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(idea?.title ?? '');
  const [format, setFormat] = useState<SocialFormat>(idea?.format ?? 'vertical');
  const [networks, setNetworks] = useState<SocialNetwork[]>(idea?.networks ?? []);
  const [duration, setDuration] = useState(
    idea?.durationSeconds ? formatDuration(idea.durationSeconds) : '',
  );
  const [description, setDescription] = useState(idea?.description ?? '');
  const [references, setReferences] = useState(idea?.referenceUrls.join('\n') ?? '');
  const [tags, setTags] = useState(idea?.tags.join(', ') ?? '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = (n: SocialNetwork, on: boolean) =>
    setNetworks((list) => SOCIAL_NETWORKS.filter((x) => (x === n ? on : list.includes(x))));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const seconds = duration.trim() === '' ? null : parseDuration(duration);
    if (duration.trim() !== '' && seconds === null) {
      setFields({ durationSeconds: 'escreva m:ss, por exemplo 0:45' });
      return;
    }
    const payload = {
      title,
      format,
      networks,
      durationSeconds: seconds,
      description,
      referenceUrls: lines(references),
      tags: tags
        .split(',')
        .map((t) => t.trim().replace(/^#/, ''))
        .filter((t) => t !== ''),
    };
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const res = idea
        ? await send('PUT', `${BASE}/${idea.id}`, payload)
        : await send('POST', BASE, payload);
      onSaved(socialIdeaSchema.parse(res));
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!idea) return;
    if (!window.confirm(`Apagar a ideia «${idea.title}»? Não se pode desfazer.`)) return;
    setBusy(true);
    try {
      await send('DELETE', `${BASE}/${idea.id}`);
      onClose();
    } catch (e) {
      setError(messageOf(e));
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          ← Banco de ideias
        </Button>
      </div>
      <Card headingLevel={2} title={idea ? idea.title : canManage ? 'Nova ideia' : 'Sugerir ideia'}>
        <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
          <Field label="Ideia" error={fields.title}>
            <Input
              value={title}
              maxLength={140}
              placeholder="Ex.: Entrevista rápida a um jogador"
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Formato" error={fields.format}>
              <Select
                value={format}
                onChange={(e) => setFormat(socialFormatSchema.parse(e.target.value))}
              >
                {SOCIAL_FORMATS.map((f) => (
                  <option key={f} value={f}>
                    {SOCIAL_FORMAT_LABELS[f]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Duração aproximada"
              optional
              hint="m:ss, por exemplo 0:45"
              error={fields.durationSeconds}
            >
              <Input
                value={duration}
                maxLength={6}
                inputMode="numeric"
                onChange={(e) => setDuration(e.target.value)}
              />
            </Field>
          </div>
          <fieldset className="flex flex-col gap-2">
            <legend className="font-sub text-label font-bold uppercase">Redes alvo</legend>
            <div className="flex flex-wrap gap-4">
              {SOCIAL_NETWORKS.map((n) => (
                <Checkbox
                  key={n}
                  label={SOCIAL_NETWORK_LABELS[n]}
                  checked={networks.includes(n)}
                  onChange={(e) => toggle(n, e.target.checked)}
                />
              ))}
            </div>
          </fieldset>
          <Field
            label="Descrição"
            optional
            hint="O guião ou o que se quer captar."
            error={fields.description}
          >
            <Textarea
              rows={5}
              value={description}
              maxLength={4000}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <Field
            label="Referências"
            optional
            hint="Links de exemplos (https://…), um por linha."
            error={fields.referenceUrls}
          >
            <Textarea rows={3} value={references} onChange={(e) => setReferences(e.target.value)} />
          </Field>
          <Field
            label="Etiquetas"
            optional
            hint="Separadas por vírgulas, por exemplo: bastidores, torneio"
            error={fields.tags}
          >
            <Input value={tags} onChange={(e) => setTags(e.target.value)} />
          </Field>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={busy}>
              {idea ? 'Guardar' : canManage ? 'Criar ideia' : 'Enviar sugestão'}
            </Button>
            {idea?.canEdit ? (
              <Button type="button" variant="ghost" disabled={busy} onClick={() => void remove()}>
                Apagar
              </Button>
            ) : null}
          </div>
          {idea ? (
            <p className="text-caption text-gu-ink-muted">
              Última alteração: {formatDateTime(idea.updatedAt)} por {idea.updatedBy}.
            </p>
          ) : null}
        </form>
      </Card>
    </div>
  );
}
